import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { recipePhotoUrl } from "@/lib/planner";
import { toast } from "sonner";

/**
 * QuickPlantRecipeDialog — an in-Planner shortcut to plant a real Grove
 * recipe seed without leaving the meal-picker flow. Kept intentionally
 * lean: just the fields required to (a) satisfy the Ideas API and (b)
 * generate a shopping list. Full recipe polish (steps, timers, extra
 * angles) still lives on /plant.
 *
 * On success, we fire onCreated(idea) so the meal picker can drop it
 * onto the target date and refresh the dashboard in one shot.
 */
export default function QuickPlantRecipeDialog({ open, onClose, onCreated, targetDateLabel }) {
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [servings, setServings] = useState("");
  const [totalTime, setTotalTime] = useState("");
  const [ingredients, setIngredients] = useState([{ name: "", qty: "", unit: "" }]);
  const [photoPath, setPhotoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTitle(""); setContext(""); setServings(""); setTotalTime("");
      setIngredients([{ name: "", qty: "", unit: "" }]);
      setPhotoPath(""); setUploading(false); setSubmitting(false);
    }
  }, [open]);

  const setIngredient = (i, patch) =>
    setIngredients((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addIngredient = () => setIngredients((rows) => [...rows, { name: "", qty: "", unit: "" }]);
  const removeIngredient = (i) =>
    setIngredients((rows) => (rows.length === 1 ? rows : rows.filter((_, idx) => idx !== i)));

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
    } catch {
      toast.error("Couldn't upload that photo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast("Give your recipe a title.");
    if (!photoPath) return toast("Add a cover photo.");
    if (!context.trim()) return toast("Add a short note about this recipe.");
    const cleanIngredients = ingredients
      .map((r) => ({ name: r.name.trim(), qty: r.qty.trim(), unit: r.unit.trim() }))
      .filter((r) => r.name);
    if (cleanIngredients.length === 0)
      return toast("Add at least one ingredient so we can build a shopping list.");

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        category: "Recipes",
        proof_label: "I made this",
        context: context.trim(),
        photo_path: photoPath,
        photos: [photoPath],
        servings: servings.trim() || null,
        total_time: totalTime.trim() || null,
        ingredients: cleanIngredients,
      };
      const { data } = await api.post("/ideas", payload);
      toast.success("Recipe planted in your Grove.");
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't plant that recipe.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-lg bg-[#F7F3ED] border-[#A87F56]/30 max-h-[92vh] overflow-y-auto"
        data-testid="planner-quick-recipe-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl text-[#2A2823]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Plant a new recipe
          </DialogTitle>
          {targetDateLabel && (
            <p className="text-xs text-[#2A2823]/60 mt-1">
              We'll add it to {targetDateLabel} once you save.
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Photo */}
          <div>
            <label className="block text-[10px] tracking-[0.3em] text-[#A87F56] mb-2">COVER PHOTO</label>
            {photoPath ? (
              <div className="relative">
                <img
                  src={recipePhotoUrl(photoPath)}
                  alt="cover"
                  className="w-full h-40 object-cover rounded-xl bg-[#EDE5DA]"
                  onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                />
                <button
                  onClick={() => setPhotoPath("")}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 text-[#8a3a2a]"
                  aria-label="Remove photo"
                  data-testid="planner-quick-recipe-remove-photo"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="planner-quick-recipe-upload"
                className="w-full h-32 rounded-xl border border-dashed border-[#A87F56]/40 bg-white/60 hover:bg-white flex flex-col items-center justify-center text-sm text-[#3E4A37]"
              >
                <ImagePlus className="w-5 h-5 mb-1" />
                {uploading ? "Uploading…" : "Upload a photo"}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
              data-testid="planner-quick-recipe-file-input"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-[10px] tracking-[0.3em] text-[#A87F56] mb-1">TITLE</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Weeknight lemon chicken"
              className="w-full rounded-lg border border-[#A87F56]/30 bg-white px-3 h-10 text-sm text-[#2A2823] focus:outline-none focus:ring-1 focus:ring-[#3E4A37]"
              data-testid="planner-quick-recipe-title"
              maxLength={120}
            />
          </div>

          {/* Servings + Total time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.3em] text-[#A87F56] mb-1">SERVES</label>
              <input
                value={servings}
                onChange={(e) => setServings(e.target.value)}
                placeholder="4"
                className="w-full rounded-lg border border-[#A87F56]/30 bg-white px-3 h-10 text-sm text-[#2A2823]"
                data-testid="planner-quick-recipe-servings"
                maxLength={20}
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] text-[#A87F56] mb-1">TOTAL TIME</label>
              <input
                value={totalTime}
                onChange={(e) => setTotalTime(e.target.value)}
                placeholder="30 min"
                className="w-full rounded-lg border border-[#A87F56]/30 bg-white px-3 h-10 text-sm text-[#2A2823]"
                data-testid="planner-quick-recipe-total-time"
                maxLength={30}
              />
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <label className="block text-[10px] tracking-[0.3em] text-[#A87F56] mb-2">INGREDIENTS</label>
            <div className="space-y-2">
              {ingredients.map((row, i) => (
                <div key={i} className="flex gap-2" data-testid={`planner-quick-recipe-ing-row-${i}`}>
                  <input
                    value={row.qty}
                    onChange={(e) => setIngredient(i, { qty: e.target.value })}
                    placeholder="1"
                    className="w-14 rounded-lg border border-[#A87F56]/30 bg-white px-2 h-10 text-sm"
                    aria-label="quantity"
                    maxLength={20}
                  />
                  <input
                    value={row.unit}
                    onChange={(e) => setIngredient(i, { unit: e.target.value })}
                    placeholder="cup"
                    className="w-16 rounded-lg border border-[#A87F56]/30 bg-white px-2 h-10 text-sm"
                    aria-label="unit"
                    maxLength={20}
                  />
                  <input
                    value={row.name}
                    onChange={(e) => setIngredient(i, { name: e.target.value })}
                    placeholder="ingredient"
                    className="flex-1 rounded-lg border border-[#A87F56]/30 bg-white px-3 h-10 text-sm"
                    aria-label="ingredient name"
                    maxLength={80}
                  />
                  <button
                    onClick={() => removeIngredient(i)}
                    disabled={ingredients.length === 1}
                    className="text-[#2A2823]/40 hover:text-[#8a3a2a] disabled:opacity-30 px-2"
                    aria-label="Remove ingredient"
                    data-testid={`planner-quick-recipe-ing-remove-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addIngredient}
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#3E4A37] hover:text-[#2f3a2a]"
              data-testid="planner-quick-recipe-add-ing"
            >
              <Plus className="w-3.5 h-3.5" /> Add ingredient
            </button>
          </div>

          {/* Context */}
          <div>
            <label className="block text-[10px] tracking-[0.3em] text-[#A87F56] mb-1">A LINE ABOUT IT</label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="What makes this one your go-to?"
              rows={2}
              className="w-full rounded-lg border border-[#A87F56]/30 bg-white px-3 py-2 text-sm text-[#2A2823]"
              data-testid="planner-quick-recipe-context"
              maxLength={600}
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="text-sm text-[#2A2823]/60 hover:text-[#2A2823] px-3 h-10"
              data-testid="planner-quick-recipe-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || uploading}
              className="inline-flex items-center gap-2 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] disabled:opacity-60 px-5 h-10 text-sm"
              data-testid="planner-quick-recipe-submit"
            >
              {submitting ? "Planting…" : "Plant & add to plan"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
